import apiClient from './client';
import type {
  TaskSubGroupWithDetails,
  TaskSubGroup,
  CreateTaskSubGroupRequest,
  UpdateTaskSubGroupRequest,
} from '../../types';

/**
 * API for managing task sub-groups (admin only)
 */
export const taskSubGroupsApi = {
  /**
   * Get all task sub-groups with details
   */
  getAll: async (
    phaseId?: string,
    includeArchived?: boolean
  ): Promise<TaskSubGroupWithDetails[]> => {
    const params = new URLSearchParams();
    if (phaseId) params.set('phase_id', phaseId);
    if (includeArchived) params.set('include_archived', 'true');
    const queryString = params.toString();
    return apiClient.get<TaskSubGroupWithDetails[]>(
      `/api/admin/sub-groups${queryString ? `?${queryString}` : ''}`
    );
  },

  /**
   * Get a single sub-group by ID
   */
  getById: async (id: string): Promise<TaskSubGroupWithDetails> => {
    return apiClient.get<TaskSubGroupWithDetails>(`/api/admin/sub-groups/${id}`);
  },

  /**
   * Create a new task sub-group
   */
  create: async (data: CreateTaskSubGroupRequest): Promise<TaskSubGroup> => {
    return apiClient.post<TaskSubGroup>('/api/admin/sub-groups', data);
  },

  /**
   * Update a task sub-group
   */
  update: async (id: string, data: UpdateTaskSubGroupRequest): Promise<TaskSubGroup> => {
    return apiClient.put<TaskSubGroup>(`/api/admin/sub-groups/${id}`, data);
  },

  /**
   * Archive a task sub-group
   */
  archive: async (id: string): Promise<{ archived: boolean }> => {
    return apiClient.post<{ archived: boolean }>(`/api/admin/sub-groups/${id}/archive`);
  },

  /**
   * Restore an archived task sub-group
   */
  restore: async (id: string): Promise<{ restored: boolean }> => {
    return apiClient.post<{ restored: boolean }>(`/api/admin/sub-groups/${id}/restore`);
  },

  /**
   * Reorder sub-groups
   */
  reorder: async (
    subGroups: { id: string; sort_order: number }[]
  ): Promise<TaskSubGroupWithDetails[]> => {
    return apiClient.put<TaskSubGroupWithDetails[]>('/api/admin/sub-groups/reorder', {
      sub_groups: subGroups,
    });
  },

  /**
   * Delete a task sub-group
   */
  delete: async (id: string): Promise<void> => {
    return apiClient.delete<void>(`/api/admin/sub-groups/${id}`);
  },

  /**
   * Get template count for a sub-group (for archive warning)
   */
  getTemplateCount: async (id: string): Promise<{ template_count: number }> => {
    return apiClient.get<{ template_count: number }>(`/api/admin/sub-groups/${id}/template-count`);
  },
};

export default taskSubGroupsApi;
